import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import ListGrid from '../../../components/ListGrid';
import EmptyScreen from '../../../components/EmptyScreen';
import Dialog from '../../../components/Dialog';
import { IconWarn } from '../../../components/dialogIcons';
import { Figure, Figures } from '../../../components/Figures';
import { cellInputStyle, CELL_CLASS } from './movementUi';
import {
  lookupItem, sortReviewRows, rowStatus, bandsOf, buildV2Csv,
  downloadCsv, todayStamp, outcomeFor, notReady,
} from './movementUtils';
import { saveRunLines, finishRun } from './movementRuns';

/*
  Movement, Review. Built to Group G's approved drawing on 8 September 2026.

  This is the screen the whole tool exists for: every line read off the
  handwritten pages, in front of a person, before any of it becomes a file that
  moves stock between hospitals.

  -- What changed from what was here, all four named in the group's README --

  - **The four figures sit on hairlines**, not in coloured tiles. Rule 5, and
    they are shared with Run History rather than written twice.
  - **A row that needs a look says WHY on the end of it.** It said "no dest",
    "item not found" and "unit: CS", which are abbreviations with a colon in
    them. With four hundred rows a tint tells you there is a problem and not
    what it is.
  - **Two downloads became one primary act and one quiet one.** The import file
    is the job somebody came here to do; the spreadsheet is a copy for their own
    records. They were an equal pair, side by side, in gold and grey.
  - **It is a list, not an HTML table.** Rule 15, and the reason it matters here
    rather than anywhere else: below 1024 a seven column table is seven columns
    of nothing, and this screen is the one a person is most likely to be holding
    a phone in front of, standing next to the pages they just photographed.

  -- And the search and the two filters, which were half here --

  There was one drop-down, destinations only. The drawing has a search across
  the item number and the location, that same location filter, and a "Needs a
  look" filter, because on a real run the three lines that need attention are
  buried in four hundred that do not.
*/

/* The drawing's own words for the four states, tinting the row's left edge. */
const TONE = { ok: 'ok', warn: 'warn', err: 'bad' };

export default function ReviewTab({
  config, rows, setRows, products, productIndex, warehouses, pageCount,
  toast, run, onRunFinished, onGoToPages, setBar,
}) {
  const [search, setSearch] = useState('');
  const [place, setPlace] = useState('all');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [askExport, setAskExport] = useState(null);   // { fileName, not }

  /* Each row's state worked out once, because it is read four times: by the
     row, by the "Need a look" figure, by the filter and by the warning. */
  const stateOf = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const product = lookupItem(r.itemNumber, products, productIndex);
      m.set(r, { product, status: rowStatus(r, product) });
    }
    return m;
  }, [rows, products, productIndex]);

  const destinations = useMemo(
    () => [...new Set(rows.map(r => r.destination).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );
  const hasUnmapped = rows.some(r => !r.destination);
  const issueCount = rows.filter(r => stateOf.get(r)?.status.kind !== 'ok').length;

  const visible = useMemo(() => {
    const q = search.trim().toUpperCase();
    return rows.filter(r => {
      if (place === '__none__' && r.destination) return false;
      if (place !== 'all' && place !== '__none__' && r.destination !== place) return false;
      if (onlyIssues && stateOf.get(r)?.status.kind === 'ok') return false;
      if (!q) return true;
      const desc = stateOf.get(r)?.product?.description || '';
      return (r.itemNumber || '').toUpperCase().includes(q)
        || (r.destination || '').toUpperCase().includes(q)
        || (r.shorthand || '').toUpperCase().includes(q)
        || desc.toUpperCase().includes(q);
    });
  }, [rows, search, place, onlyIssues, stateOf]);

  const bands = useMemo(() => bandsOf(visible), [visible]);

  // Rows are edited in place, by identity, so sorting for the screen never
  // changes which row an edit lands on.
  function updateRow(row, patch) {
    setRows(prev => prev.map(r => (r === row ? { ...r, ...patch } : r)));
  }

  /*
    **A copy for your records**, which is the quiet act. It is the whole review
    as a spreadsheet, including the lines that will be left out of the file,
    because it is the thing somebody keeps rather than the thing the receiving
    system reads.
  */
  function saveACopy() {
    if (!rows.length) { toast.error('There are no lines to save.'); return; }
    const data = [['Date', 'Written on the page', 'Goes to', 'Item number', 'Description',
                   'Quantity', 'Unit', 'State', 'Page']];
    for (const r of sortReviewRows(rows)) {
      const { product, status } = stateOf.get(r) || {};
      data.push([r.date, r.shorthand, r.destination, r.itemNumber,
                 product ? product.description : '', r.quantity, r.unit,
                 status ? status.label : '', r.imageName]);
    }
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 14 }, { wch: 18 }, { wch: 38 }, { wch: 22 }, { wch: 48 },
                   { wch: 8 }, { wch: 8 }, { wch: 18 }, { wch: 24 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Movement Review');
    XLSX.writeFile(wb, `OnHand_Movement_Review_${todayStamp()}.xlsx`);
    toast.success(rows.length + (rows.length === 1 ? ' line saved.' : ' lines saved.'));
  }

  function makeTheFileAsked() {
    if (!rows.length) { toast.error('There are no lines to put in a file.'); return; }
    /* The catalog is right here, so the count can include an item number
       nothing recognises rather than leaving it to the receiving system. */
    const not = notReady(rows, n => !!lookupItem(n, products, productIndex));
    const fileName = `OnHand_Movement_Import_${todayStamp()}.csv`;
    if (not.total) { setAskExport({ fileName, not }); return; }
    makeTheFile(fileName);
  }

  function makeTheFile(fileName) {
    setAskExport(null);
    const { csv, written } = buildV2Csv(rows, config.sourceWarehouse, config.transferredBy);
    if (!written) { toast.error('Every line was left out, so there is nothing to put in a file.'); return; }
    downloadCsv(csv, fileName);
    toast.success(`${written} line${written === 1 ? '' : 's'} exported.`);
    keepTheRecord(fileName, written);
  }

  /*
    **The file is made, so the review is done, so the photographs go.**

    This is the owner's rule of 5 September 2026 carried out at the one moment
    it can be: making the import file is what finishing a review means. Three
    things happen, in this order and no other:

      1. every line is written down, with what happened to it
      2. the run is marked finished
      3. the photographs are deleted

    **The file is already on the person's machine before any of it starts.** The
    export is the thing they came for and it must never wait on, or be undone
    by, the record keeping. If step 1 fails the pictures stay where they are and
    the log says so, which is recoverable; the other order would delete the only
    copy of a page whose lines were never saved.
  */
  async function keepTheRecord(fileName, written) {
    if (!run) return;
    setKeeping(true);
    const lines = sortReviewRows(rows).map(r => {
      const { outcome, why } = outcomeFor(r);
      const product = lookupItem(r.itemNumber, products, productIndex);
      return {
        pageId: r.pageId || null,
        written: r.shorthand || '',
        date: r.date || '',
        destination: r.destination || '',
        itemNumber: r.itemNumber || '',
        description: product ? product.description : '',
        quantity: r.quantity,
        unit: r.unit || '',
        outcome,
        why,
      };
    });

    const { error } = await saveRunLines({ run, rows: lines, fileName });
    if (error) {
      setKeeping(false);
      toast.warning('The file was made, but this run could not be written to Run History: '
        + error.message + ' The photographs have been left where they are.');
      return;
    }

    const { error: finErr } = await finishRun(run.id);
    setKeeping(false);
    if (finErr) {
      toast.warning('This run is in Run History, but its photographs could not be deleted: '
        + finErr.message + ' They are still held and can be removed again.');
    } else {
      toast.success(`${written} line${written === 1 ? '' : 's'} exported. This run is in Run History and its photographs have been deleted.`);
    }
    if (onRunFinished) onRunFinished();
  }

  /*
    The bar's own row, handed up so there is still one bar (rule 26). The
    drawing puts the count, the search, both filters and both acts in it, and
    below 1024 the shell folds them into the filters chip and the overflow of
    its own accord.
  */
  useEffect(() => {
    if (!setBar) return;
    setBar({
      meta: rows.length ? rows.length + (rows.length === 1 ? ' line' : ' lines') : null,
      search: rows.length
        ? { value: search, onChange: setSearch, placeholder: 'Search an item or a location' }
        : null,
      /*
        **The two filters live behind the Filters button, the two acts do not.**
        On a phone every control handed up as an action gets a row of its own,
        and Review had four of them. The bar counts what is set, so a filter left
        on is visible with the drawer shut.
      */
      filterCount: (place !== 'all' ? 1 : 0) + (onlyIssues ? 1 : 0),
      drawer: rows.length ? (
        <>
          <span className="tb-lbl">Location</span>
          <select className={'tb-select' + (place !== 'all' ? ' set' : '')}
            value={place} onChange={e => setPlace(e.target.value)}>
            <option value="all">All locations</option>
            {destinations.map(d => <option key={d} value={d}>{d}</option>)}
            {hasUnmapped && <option value="__none__">No location yet</option>}
          </select>
          {/* A filter for the three lines in four hundred that need a person.
              Offered only when there are any, because a filter that can only
              ever empty the screen is a control that does nothing. */}
          {issueCount > 0 && (
            <button className={'tb-btn' + (onlyIssues ? ' tb-btn-primary' : ' tb-btn-ghost')}
              onClick={() => setOnlyIssues(v => !v)}>
              {onlyIssues ? 'Showing the ' + issueCount + ' that need a look' : 'Needs a look'}
            </button>
          )}
          <span className="tb-grow" />
          {(place !== 'all' || onlyIssues) && (
            <button className="tb-btn tb-btn-danger"
              onClick={() => { setPlace('all'); setOnlyIssues(false); }}>
              Clear filters
            </button>
          )}
        </>
      ) : null,
      /* The copy is the quiet act, so it folds behind the "..." on a phone.
         Making the file is what you came for and never folds (rule 33). */
      quiet: rows.length ? (
        <button className="tb-btn tb-btn-ghost" onClick={saveACopy}>A copy for your records</button>
      ) : null,
      actions: rows.length ? (
        <button className="tb-btn tb-btn-primary" onClick={makeTheFileAsked} disabled={keeping}>
          {keeping ? 'Saving the run...' : 'Make the file'}
        </button>
      ) : null,
    });
    // saveACopy and makeTheFileAsked read `rows` and `stateOf`, which the first
    // two dependencies already stand for.
  }, [rows, search, place, onlyIssues, issueCount, keeping, destinations.length, hasUnmapped, setBar]); // eslint-disable-line

  /* -- Nothing has been read yet ---------------------------------------- */
  if (!rows.length) {
    return (
      <EmptyScreen
        title="Nothing to check yet"
        says={'Once pages have been read, every line comes here to be checked and corrected '
            + 'before it becomes a file. Read some pages first.'}
        action={onGoToPages ? { label: 'Go to Pages', onClick: onGoToPages } : null}
      />
    );
  }

  /*
    **What is wrong, said once at the top, in the words of the things that are
    wrong.** The drawing's own sentence, built from what is actually there
    rather than printed whatever the counts. It is the difference between "3
    lines need a look" and knowing that one of them is a shorthand nobody has
    ever mapped.
  */
  const kinds = [];
  const noLocation = rows.filter(r => !r.destination).length;
  const notInCatalog = rows.filter(r => r.destination && !stateOf.get(r)?.product).length;
  const unitWritten = rows.filter(r => r.destination && stateOf.get(r)?.product && r.unit).length;
  if (noLocation) {
    kinds.push(noLocation === 1
      ? "one page's shorthand has never been mapped"
      : noLocation + ' lines are written in shorthand nobody has mapped');
  }
  if (notInCatalog) {
    kinds.push(notInCatalog === 1
      ? 'one item number is not in the catalog'
      : notInCatalog + ' item numbers are not in the catalog');
  }
  if (unitWritten) {
    kinds.push(unitWritten === 1
      ? 'one line has a unit written beside the quantity'
      : unitWritten + ' lines have a unit written beside the quantity');
  }

  return (
    <div>
      <Figures>
        <Figure n={pageCount} label="Pages" />
        <Figure n={rows.length} label="Lines" />
        <Figure n={destinations.length} label="Locations" />
        <Figure n={issueCount} label="Need a look" tone={issueCount ? 'var(--critical-ink)' : undefined} />
      </Figures>

      {issueCount > 0 && (
        <div className="mv-note held">
          <div className="mv-note-title">
            {issueCount === 1
              ? '1 line needs a look before this becomes a file'
              : issueCount + ' lines need a look before this becomes a file'}
          </div>
          <div className="mv-note-says">
            {kinds.join(', ').replace(/^./, c => c.toUpperCase())}. Lines with something wrong are
            left out of the file rather than guessed at.
          </div>
        </div>
      )}

      <div className="rplist">
        {visible.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            Nothing matches what you are looking for.
          </div>
        ) : (
          <ListGrid
            columns={[
              { label: 'Date', width: '82px' },
              { label: 'Written', width: 'minmax(0,0.9fr)' },
              { label: 'Goes to', width: 'minmax(0,1.3fr)' },
              { label: 'Item number', width: 'minmax(0,0.9fr)' },
              { label: 'Description', width: 'minmax(0,1.4fr)' },
              { label: 'Qty', width: '96px', align: 'right' },
              { label: 'State', width: '132px' },
            ]}
            /*
              **Banded under the location each line goes to.** One list rather
              than one per band, so a person reading down it is reading one
              thing. The band a line is in is the single most useful fact about
              it: this screen's job is answering "did all of NRMC's come out
              right".
            */
            groups={bands.map(b => ({
              key: b.key,
              header: (
                <div className="rpband">
                  {b.territory && <span className="rpband-t">{b.territory}</span>}
                  <span className="rpband-n">{b.name}</span>
                </div>
              ),
              rows: b.rows,
            }))}
            /*
              **The key cannot contain anything you can type into.** It had the
              item number in it, which is an editable box on the same row: React
              would have thrown the row away and built a new one on every
              keystroke, so the box lost the cursor after each character. The
              line's place on the page is the one thing about it that never
              changes.
            */
            keyOf={(r, i) => (r.pageId || 'p') + '-' + (r.pageOrder ?? i)}
            rowClass={r => 'mv-line ' + TONE[stateOf.get(r)?.status.kind || 'ok']}
            cells={r => {
              const { product, status } = stateOf.get(r) || {};
              return [
                <span className="mv-written">{r.date}</span>,
                <input className={CELL_CLASS} style={cellInputStyle} value={r.shorthand}
                  onChange={e => updateRow(r, { shorthand: e.target.value })} />,
                /* Whatever is stored stays offered even when it is not an
                   active location, so opening this list never quietly wipes a
                   good destination. Same fault, same fix, as Account Mappings. */
                <select className={CELL_CLASS} style={cellInputStyle} value={r.destination || ''}
                  onChange={e => updateRow(r, { destination: e.target.value })}>
                  <option value="">Pick a location</option>
                  {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                  {r.destination && !warehouses.includes(r.destination) && (
                    <option value={r.destination}>{r.destination} (not an active location)</option>
                  )}
                </select>,
                <input className={CELL_CLASS} style={cellInputStyle} value={r.itemNumber}
                  onChange={e => updateRow(r, { itemNumber: e.target.value })} />,
                /*
                  **Blank, not "Not in the catalog", and only here.** The State
                  column on the same row already says it, in colour, and the
                  drawing leaves this cell empty for exactly that reason. The
                  stacked shape below has no State column beside the
                  description, so it says it there.
                */
                product ? <span className="rpdesc">{product.description}</span> : <span />,
                /* The unit sits INSIDE the quantity, which is the drawing and
                   also the truth: a case of twenty goes into the file as a
                   plain 1, and putting the unit anywhere else hides that. */
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                  <input className={CELL_CLASS} style={{ ...cellInputStyle, width: 58, textAlign: 'right' }}
                    value={r.quantity} onChange={e => updateRow(r, { quantity: e.target.value })} />
                  {r.unit && <span className="mv-unit">{r.unit}</span>}
                </span>,
                <span className={'mv-outcome ' + TONE[status?.kind || 'ok']}>{status?.label}</span>,
              ];
            }}
            stack={r => {
              const { product, status } = stateOf.get(r) || {};
              return (
                <span>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <input className={CELL_CLASS} style={{ ...cellInputStyle, flex: 1, minWidth: 0, fontWeight: 800 }}
                      value={r.itemNumber} onChange={e => updateRow(r, { itemNumber: e.target.value })} />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <input className={CELL_CLASS} style={{ ...cellInputStyle, width: 62, textAlign: 'right' }}
                        value={r.quantity} onChange={e => updateRow(r, { quantity: e.target.value })} />
                      {r.unit && <span className="mv-unit">{r.unit}</span>}
                    </span>
                  </span>
                  <span style={{ display: 'block', fontSize: 13, margin: '7px 0 7px' }}>
                    {product ? product.description : <span className="rpdash">Not in the catalog</span>}
                  </span>
                  <select className={CELL_CLASS} style={cellInputStyle} value={r.destination || ''}
                    onChange={e => updateRow(r, { destination: e.target.value })}>
                    <option value="">Pick a location</option>
                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                    {r.destination && !warehouses.includes(r.destination) && (
                      <option value={r.destination}>{r.destination} (not an active location)</option>
                    )}
                  </select>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <span className="mv-written">{r.shorthand}</span>
                    <span className={'mv-outcome ' + TONE[status?.kind || 'ok']}>{status?.label}</span>
                  </span>
                </span>
              );
            }}
          />
        )}
      </div>

      {/*
        **One dialog where there were two browser boxes.** Rule 34, and Group
        G's approved drawing `d-export-issues-*.png`.

        **What it says that a browser box cannot.** It names each kind of
        problem with its own count, says how many of the lines the file will
        actually hold if it is made as it stands, and offers the way back as
        well as the way on. The old pair printed a wall of sentences, one per
        row, and asked twice about the same file.
      */}
      {askExport && (
        <Dialog
          kind="question"
          icon={<IconWarn />}
          title={askExport.not.total + (askExport.not.total === 1 ? ' line is not ready' : ' lines are not ready')}
          record={askExport.fileName}
          onClose={() => setAskExport(null)}
          footer={
            <>
              <button className="btn btn-quiet" onClick={() => setAskExport(null)}>Go back and fix them</button>
              <button className="btn btn-primary" onClick={() => makeTheFile(askExport.fileName)}>
                Make the file anyway
              </button>
            </>
          }
        >
          <div className="nr-list">
            {[
              [askExport.not.noLocation,  'has no location',  'have no location'],
              [askExport.not.noItemNumber, 'has no item number', 'have no item number'],
              [askExport.not.notInCatalog, 'is not in the catalog', 'are not in the catalog'],
              [askExport.not.badQuantity, 'has no usable quantity', 'have no usable quantity'],
              [askExport.not.unitWritten, 'has a unit written beside the quantity',
                                          'have a unit written beside the quantity'],
            ].filter(([n]) => n > 0).map(([n, one, many]) => (
              <div className="nr-row" key={many}>
                <span className="nr-n">{n}</span>
                <span>{n === 1 ? 'line ' + one : 'lines ' + many}</span>
              </div>
            ))}
          </div>

          {/*
            **The unit line is the one that goes IN**, and saying so is the
            whole of fault F. A case of twenty leaves as a one, and this is the
            last moment anybody could stop it.

            Amber whenever anything is not ready, which is the only time this
            dialog is shown. It read blue when the only problems were unknown
            item numbers, which is a calm colour for a file that will be
            rejected.
          */}
          <div className="mv-note held">
            <div className="mv-note-title">
              {askExport.not.willHold === askExport.not.of
                ? 'The file will hold all ' + askExport.not.of + ' lines'
                : 'The file will hold ' + askExport.not.willHold + ' of the ' + askExport.not.of + ' lines'}
            </div>
            <div className="mv-note-says">
              {askExport.not.willHold < askExport.not.of
                && 'A line with no location, no item number or no usable quantity is left out rather than guessed at. '}
              {askExport.not.notInCatalog > 0
                && (askExport.not.notInCatalog === 1
                  ? 'The line whose item number is not in the catalog goes in as written, and the system reading the file is what will reject it. '
                  : 'The ' + askExport.not.notInCatalog + ' lines whose item numbers are not in the catalog go in as written, and the system reading the file is what will reject them. ')}
              {askExport.not.unitWritten > 0
                && (askExport.not.unitWritten === 1
                  ? 'The line with a unit written on it DOES go in, as a plain number, so a case of twenty leaves as a one. '
                  : 'The ' + askExport.not.unitWritten + ' lines with a unit written on them DO go in, as plain numbers, so a case of twenty leaves as a one. ')}
              Fix them first and the file holds all {askExport.not.of}.
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
